/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* Copyright 2026 Bytefinger Technology (HK) Co., Limited
 * Licensed under the Apache License, Version 2.0 */
import { type IMessage, type MdBoxProps } from '@adp-common/chat-uikit-shared';
import { ContentType } from '@adp-common/chat-core/message/types';

import { isMultimodalContentListLike, isTextMixItem } from './multimodal';

const TEXT_SEPARATOR = '\n\n';

const textParts = (message: IMessage): string[] =>
  isMultimodalContentListLike(message.content_obj)
    ? message.content_obj.item_list
        .filter(isTextMixItem)
        .map(item => item.text)
        .filter(text => typeof text === 'string' && text.length > 0)
    : [];

/** Visible Markdown only; never expose the serialized mix payload to copy. */
export const getMessageText = (message: IMessage): string => {
  if (message.content_type === ContentType.Text) {
    return message.content;
  }
  return message.content_type === ContentType.Mix
    ? textParts(message).join(TEXT_SEPARATOR)
    : '';
};

/** Citation offsets are UTF-16 positions in the original text, before separators. */
export const projectMultimodalText = (
  message: IMessage,
  mdBoxProps?: MdBoxProps,
): { message: IMessage; mdBoxProps?: MdBoxProps } => {
  const parts = textParts(message);
  const mapOffset = (offset: number, start = false) => {
    let end = 0;
    let separators = 0;
    for (const part of parts.slice(0, -1)) {
      end += part.length;
      // End markers on a boundary belong to the preceding text part.
      if (offset > end || (start && offset === end)) {
        separators += TEXT_SEPARATOR.length;
      }
    }
    return offset + separators;
  };
  return {
    message: {
      ...message,
      content: getMessageText(message),
      broken_pos:
        typeof message.broken_pos !== 'number'
          ? message.broken_pos
          : mapOffset(message.broken_pos),
    },
    mdBoxProps: mdBoxProps && {
      ...mdBoxProps,
      insertedElements: mdBoxProps.insertedElements?.map(item => ({
        ...item,
        range:
          typeof item.range === 'number'
            ? mapOffset(item.range)
            : ([mapOffset(item.range[0], true), mapOffset(item.range[1])] as [
                number,
                number,
              ]),
      })),
    },
  };
};
